import { useEffect, useCallback, useRef } from 'react'
import {
  useTonConnectUI,
  useTonWallet,
  useTonAddress,
  type Wallet,
} from '@tonconnect/ui-react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from '@/shared/toaster'
import { accessToken } from '@/shared/auth'
import { getUserControllerGetMeQueryKey } from '@/api/users/users'
import {
  useWalletControllerGetWallets,
  getWalletControllerGetWalletsQueryKey,
} from '@/api/wallet/wallet'
import { useWalletVerification } from './use-wallet-verification'

const BASE_URL =
  import.meta.env.VITE_USE_PROXY === 'true'
    ? '/api'
    : import.meta.env.VITE_API_URL || ''

// ГЛОБАЛЬНЫЕ блокировки - вне React, один инстанс на всё приложение
const usedPayloads = new Set<string>()
let isVerifying = false

export const useWallet = () => {
  const [tonConnectUI] = useTonConnectUI()
  const wallet = useTonWallet()
  const userFriendlyAddress = useTonAddress()
  const queryClient = useQueryClient()

  // Получаем данные кошельков с backend
  const { data: backendWallets } = useWalletControllerGetWallets({
    query: {
      enabled: !!accessToken.value,
    },
  })

  // Получаем активный кошелек (isActive: true)
  const activeWallet = backendWallets?.find((w) => w.isActive)

  // isConnected определяется комбинацией локального wallet И активного кошелька на backend
  const isConnected = !!wallet && !!activeWallet

  // Хук для верификации (useMutation внутри)
  const verifyMutation = useWalletVerification()

  // Ref для доступа к mutation в колбэке (избегаем stale closure)
  const verifyMutationRef = useRef(verifyMutation)
  verifyMutationRef.current = verifyMutation

  /**
   * Верификация кошелька на бэкенде
   */
  const verifyWallet = useCallback(
    async (connectedWallet: Wallet) => {
      const tonProof = connectedWallet.connectItems?.tonProof

      // Нет proof - это reconnect из localStorage, не верифицируем
      if (!tonProof || !('proof' in tonProof) || !tonProof.proof) {
        return
      }

      const payload = tonProof.proof.payload

      // ГЛОБАЛЬНАЯ проверка - payload уже использован?
      if (usedPayloads.has(payload)) {
        return
      }

      // СРАЗУ блокируем ГЛОБАЛЬНО (синхронно!)
      usedPayloads.add(payload)

      // ГЛОБАЛЬНАЯ проверка - уже верифицируем?
      if (isVerifying) {
        return
      }
      isVerifying = true

      try {
        await verifyMutationRef.current.mutateAsync({
          wallet: connectedWallet,
          onError: async () => {
            await tonConnectUI.disconnect()
          },
        })
      } finally {
        isVerifying = false
      }
    },
    [tonConnectUI],
  )

  /**
   * Подключение кошелька с TON Proof
   */
  const connect = useCallback(async () => {
    try {
      // Получаем payload с backend
      const res = await fetch(`${BASE_URL}/wallet/generate-payload`, {
        method: 'POST',
        headers: accessToken.value
          ? { Authorization: `Bearer ${accessToken.value}` }
          : undefined,
        credentials: 'include',
      })

      if (!res.ok) {
        throw new Error('Не удалось получить payload для подключения')
      }

      const { payload } = await res.json()
      if (!payload) {
        throw new Error('Payload не получен')
      }

      // Настраиваем TON Connect для запроса proof
      tonConnectUI.setConnectRequestParameters({
        state: 'ready',
        value: { tonProof: payload },
      })

      // Открываем модальное окно подключения
      await tonConnectUI.openModal()
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Ошибка при подключении кошелька'
      toast.error(message)
      throw error
    }
  }, [tonConnectUI])

  /**
   * Отключение кошелька
   */
  const disconnect = useCallback(async () => {
    try {
      await tonConnectUI.disconnect()
      // Обновляем данные на фронте
      queryClient.invalidateQueries({
        queryKey: getWalletControllerGetWalletsQueryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: getUserControllerGetMeQueryKey(),
      })
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Ошибка при отключении кошелька'
      toast.error(message)
      throw error
    }
  }, [tonConnectUI, queryClient])

  // Ref для доступа к verifyWallet без пересоздания слушателя
  const verifyWalletRef = useRef(verifyWallet)
  verifyWalletRef.current = verifyWallet

  /**
   * Подписка на события TON Connect
   */
  useEffect(() => {
    const unsubscribe = tonConnectUI.onStatusChange(async (connectedWallet) => {
      if (connectedWallet) {
        await verifyWalletRef.current(connectedWallet)
      }
    })

    return () => unsubscribe()
  }, [tonConnectUI])

  return {
    // Состояние
    wallet,
    isConnected,
    isVerifying: verifyMutation.isPending,
    address: userFriendlyAddress,

    // Методы
    connect,
    disconnect,
  }
}
